import React, { useState, useEffect } from "react";
import api from "./Api";

/**
 * NewsletterSignup
 * - Default mode (adminMode=false): public subscribe form
 * - adminMode=true: subscriber management (ROLE_ADMIN only)
 */
const NewsletterSignup = ({ adminMode = false }) => {
  // Subscribe form state
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState(null); // null | "success" | "error" | "exists"
  const [subLoading, setSubLoading] = useState(false);

  // Admin state
  const [subscribers, setSubscribers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    if (adminMode) fetchSubscribers();
  }, [adminMode]);

  const fetchSubscribers = async () => {
    setAdminLoading(true);
    try {
      const resp = await api.get("/newsletter/subscribers");
      setSubscribers(resp || []);
    } catch (err) {
      console.error("Failed to load subscribers:", err);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubStatus("invalid");
      return;
    }
    setSubLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setSubStatus("success");
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        setSubStatus("exists");
      } else {
        setSubStatus("error");
      }
    } finally {
      setSubLoading(false);
    }
  };

  const handleUnsubscribe = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/newsletter/unsubscribe/${id}`);
      setSubscribers(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert("Failed to remove subscriber.");
    }
  };

  /* ---- Admin view ---- */
  if (adminMode) {
    return (
      <div style={{ padding: 8 }}>
        <h2 style={{ marginBottom: 20, color: "var(--text-color)" }}>Newsletter Subscribers</h2>

        <div style={{
          display: "inline-block", padding: "12px 20px", borderRadius: 10,
          background: "var(--card-bg, #fff)", boxShadow: "0 2px 8px var(--shadow-color, rgba(0,0,0,0.07))",
          marginBottom: 24, fontWeight: 600, color: "var(--primary-color, #224abe)",
        }}>
          {subscribers.length} subscriber{subscribers.length !== 1 ? "s" : ""}
        </div>

        {adminLoading ? (
          <p style={{ color: "var(--muted-color)" }}>Loading…</p>
        ) : subscribers.length === 0 ? (
          <p style={{ color: "var(--muted-color)" }}>No subscribers yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ color: "var(--muted-color)", textAlign: "left" }}>
                  {["Email", "Subscribed At", "Active", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", borderBottom: "2px solid var(--border-color, #ddd)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s, i) => (
                  <tr key={s.id} style={{ background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)" }}>
                    <td style={{ padding: "9px 12px", color: "var(--text-color)" }}>{s.email}</td>
                    <td style={{ padding: "9px 12px", color: "var(--muted-color)" }}>
                      {s.subscribedAt ? new Date(s.subscribedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <span style={{
                        padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: s.active ? "#d1fae5" : "#fee2e2",
                        color: s.active ? "#065f46" : "#991b1b",
                      }}>{s.active ? "Active" : "Inactive"}</span>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <button
                        onClick={() => handleUnsubscribe(s.id)}
                        style={{
                          padding: "4px 12px", borderRadius: 6, border: "none",
                          background: "#fee2e2", color: "#991b1b", cursor: "pointer", fontWeight: 600, fontSize: 12,
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  /* ---- Public subscribe form ---- */
  const msgMap = {
    success: { text: "🎉 You're subscribed! Welcome to Digital Journaling.", color: "#065f46", bg: "#d1fae5" },
    exists: { text: "You're already subscribed with this email.", color: "#92400e", bg: "#fef3c7" },
    error: { text: "Something went wrong. Please try again.", color: "#991b1b", bg: "#fee2e2" },
    invalid: { text: "Please enter a valid email address.", color: "#991b1b", bg: "#fee2e2" },
  };

  return (
    <div style={{
      background: "linear-gradient(135deg, #224abe, #4a6cf7)",
      borderRadius: 16, padding: "32px 28px", textAlign: "center",
      color: "#fff", maxWidth: 520, margin: "0 auto",
    }}>
      <h3 style={{ marginBottom: 8, fontSize: "1.3rem" }}>Stay Updated</h3>
      <p style={{ marginBottom: 24, opacity: 0.88, fontSize: 14 }}>
        Subscribe to Digital Journaling and get fresh articles delivered to your inbox.
      </p>

      {subStatus && subStatus !== "success" ? (
        <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 16, background: msgMap[subStatus]?.bg, color: msgMap[subStatus]?.color, fontSize: 14 }}>
          {msgMap[subStatus]?.text}
        </div>
      ) : subStatus === "success" ? (
        <div style={{ padding: "12px 16px", borderRadius: 8, background: "#d1fae5", color: "#065f46", fontSize: 14 }}>
          {msgMap.success.text}
        </div>
      ) : (
        <form onSubmit={handleSubscribe} style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setSubStatus(null); }}
            placeholder="Enter your email…"
            required
            style={{
              flex: "1 1 220px", padding: "12px 16px", borderRadius: 10,
              border: "none", fontSize: 14, outline: "none",
              background: "rgba(255,255,255,0.95)", color: "#222",
            }}
          />
          <button
            type="submit"
            disabled={subLoading}
            style={{
              padding: "12px 24px", borderRadius: 10, border: "none",
              background: "#fff", color: "#224abe", fontWeight: 700,
              cursor: "pointer", fontSize: 14, flexShrink: 0,
              opacity: subLoading ? 0.7 : 1,
            }}
          >
            {subLoading ? "Subscribing…" : "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
};

export default NewsletterSignup;
